"use client";

import { Card } from "../ui/Card";
import { UserProfile } from "../../data/profile/types";
import {
  getGradeInfo,
  uploadAvatar,
  deleteAvatar,
} from "../../lib/profile/api";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  Calendar,
  TrendingUp,
  Star,
  MapPin,
  MoreVertical,
  Edit,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useState, useRef, useEffect } from "react";

interface ProfileStatsProps {
  user: UserProfile;
  isOwnProfile?: boolean;
  onAvatarUpdate?: (photoURL: string) => void;
}

export function ProfileStats({
  user,
  isOwnProfile = false,
  onAvatarUpdate,
}: ProfileStatsProps) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatDate = (date: any) => {
    if (!date) return "";
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return formatDistanceToNow(dateObj, { addSuffix: true, locale: ko });
  };

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAvatarChange = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file && onAvatarUpdate) {
        try {
          // 파일 크기 체크 (5MB 제한)
          if (file.size > 5 * 1024 * 1024) {
            toast.error("파일 크기는 5MB 이하여야 합니다.");
            return;
          }

          // 이미지 파일 타입 체크
          if (!file.type.startsWith("image/")) {
            toast.error("이미지 파일만 업로드 가능합니다.");
            return;
          }

          // Firebase Storage에 업로드
          const result = await uploadAvatar(user.uid, file);

          if (result.success && result.data) {
            onAvatarUpdate(result.data);
          } else {
            toast.error(result.error || "업로드에 실패했습니다.");
          }
        } catch (error) {
          console.error("아바타 업로드 실패:", error);
          toast.error("업로드 중 오류가 발생했습니다.");
        }
      }
    };
    input.click();
    setShowMenu(false);
  };

  const handleAvatarDelete = async () => {
    if (!onAvatarUpdate) return;

    try {
      // 실제 Firebase Storage와 Firestore에서 삭제
      const result = await deleteAvatar(user.uid, user.photoURL);

      if (result.success) {
        onAvatarUpdate(""); // UI 업데이트
        toast.success("프로필 사진이 삭제되었습니다.");
      } else {
        toast.error(result.error || "프로필 사진 삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("프로필 사진 삭제 실패:", error);
      toast.error("프로필 사진 삭제 중 오류가 발생했습니다.");
    }

    setShowMenu(false);
  };

  return (
    <Card>
      <div className="p-5">
        {/* 프로필 이미지 - 상단 여백 24px */}
        <div className="pt-6 pb-4 flex justify-center">
          <div className="relative flex-shrink-0">
            <div
              className={`w-24 h-24 rounded-full overflow-hidden bg-gray-100 ${
                isOwnProfile
                  ? "cursor-pointer hover:opacity-80 transition-opacity"
                  : ""
              }`}
              onClick={isOwnProfile ? handleAvatarChange : undefined}
            >
              {user.photoURL ? (
                <img
                  src={user.photoURL.includes('?') ? `${user.photoURL}&t=${Date.now()}` : `${user.photoURL}?t=${Date.now()}`}
                  alt={user.nickname}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // 이미지 로드 실패 시 플레이스홀더 표시
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const placeholder = target.parentElement?.querySelector('.profile-placeholder') as HTMLElement;
                    if (placeholder) {
                      placeholder.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div className={`w-full h-full flex flex-col items-center justify-center text-gray-400 profile-placeholder ${user.photoURL ? 'hidden' : ''}`}>
                <span className="text-3xl font-bold mb-1">
                  {user.nickname.charAt(0).toUpperCase()}
                </span>
                {isOwnProfile && (
                  <span className="text-xs opacity-60">📷</span>
                )}
              </div>
            </div>

            {/* 점점점 메뉴 */}
            {isOwnProfile && (
              <div className="relative" ref={menuRef}>
                <button
                  className="absolute -top-2 -right-2 w-6 h-6 bg-white hover:bg-gray-50 rounded-full flex items-center justify-center transition-colors shadow-md"
                  onClick={e => {
                    e.stopPropagation();
                    setShowMenu(!showMenu);
                  }}
                  title="프로필 사진 메뉴"
                >
                  <MoreVertical className="w-3 h-3 text-gray-800" />
                </button>

                {/* 드롭다운 메뉴 */}
                {showMenu && (
                  <div className="absolute top-6 right-0 bg-white rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
                    <button
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      onClick={handleAvatarChange}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      사진 변경
                    </button>
                    {user.photoURL && (
                      <button
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                        onClick={handleAvatarDelete}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        사진 삭제
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 닉네임 - 가독성 강화 */}
        <div className="text-center mb-4">
          <h1 className="text-[20px] font-semibold text-gray-900 mb-4">
            {user.nickname}
          </h1>

          {/* 지역 · 가입일 - 같은 row에 배치 */}
          <div className="flex items-center justify-center gap-4 text-[14px] text-[#666]">
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" />
              <span>{user.region || "지역 미설정"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(user.createdAt)} 가입</span>
            </div>
          </div>
        </div>

        {/* 활동 지표 - 시각적 강조 */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
          <div className="text-center">
            <div className="text-[20px] font-bold text-gray-900 mb-1">
              {user.tradesCount || 0}
            </div>
            <div className="text-[12px] text-[#777]">총 거래</div>
          </div>

          <div className="text-center">
            <div className="text-[20px] font-bold text-gray-900 mb-1">
              {user.reviewsCount || 0}
            </div>
            <div className="text-[12px] text-[#777]">받은 후기</div>
          </div>

          <div className="text-center">
            <div className="text-[20px] font-bold text-gray-900 mb-1">
              {user.responseRate || 0}%
            </div>
            <div className="text-[12px] text-[#777]">응답률</div>
          </div>
        </div>
      </div>
    </Card>
  );
}
